"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.downloadPdf = exports.openStudyReportPdf = void 0;
const jspdf_1 = __importDefault(require("jspdf"));
const FONT_FILE_NAME = 'NotoSansKR-Regular.ttf';
const FONT_FAMILY = 'NotoSansKR';
const PAGE_MARGIN_X = 10;
const PAGE_TOP = 10;
const PAGE_BOTTOM = 12;
const FOOTER_SPACE = 8;
const SUMMARY_TITLE_HEIGHT = 14;
const SUMMARY_CARD_HEIGHT = 48;
const SUMMARY_BLOCK_GAP = 8;
const SUMMARY_BAR_HEIGHT = 5;
const SUMMARY_BAR_COLOR = [139, 195, 208];
const HISTORY_TITLE_HEIGHT = 10;
const SECTION_TITLE_HEIGHT = 8;
const SECTION_TITLE_TABLE_GAP = 2;
const TABLE_HEADER_HEIGHT = 8;
const TABLE_ROW_HEIGHT = 8;
const BLOCK_GAP = 6;
let fontLoadPromise = null;
const toBase64 = (bytes) => {
    let binary = '';
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }
    return btoa(binary);
};
const loadKoreanFontBase64 = async () => {
    if (!fontLoadPromise) {
        fontLoadPromise = fetch(`/libs/${FONT_FILE_NAME}`)
            .then(res => {
            if (!res.ok) {
                throw new Error(`Failed to load font: ${res.status}`);
            }
            return res.arrayBuffer();
        })
            .then(buffer => toBase64(new Uint8Array(buffer)));
    }
    return fontLoadPromise;
};
const trimText = (pdf, text, maxWidth) => {
    if (pdf.getTextWidth(text) <= maxWidth)
        return text;
    let result = text;
    while (result.length > 1 && pdf.getTextWidth(`${result}...`) > maxWidth) {
        result = result.slice(0, -1);
    }
    return `${result}...`;
};
const formatDate = (value) => {
    if (!value)
        return '-';
    const asString = String(value);
    if (asString.length >= 10)
        return asString.slice(0, 10);
    return '-';
};
const getAttendanceStatusText = (item) => {
    if (item?.status === 'late' || item?.attendance_status === 'late' || item?.late === true)
        return '지각';
    if (item?.done)
        return '출석';
    return '결석';
};
const getDurationText = (item) => {
    // 지각(late)은 done=false 지만 실제 참여시간이 존재한다 — 출석(done)과 동일하게 표시.
    // (교사 리포트 DSTL006 은 done 게이트 없이 표시 중 — 학생 리포트만 누락돼 있던 것 수정)
    const isLate = item?.status === 'late' || item?.attendance_status === 'late' || item?.late === true;
    if (!item?.done && !isLate)
        return '-';
    const attendMinutes = Number(item?.attend_time);
    if (Number.isFinite(attendMinutes) && attendMinutes >= 0)
        return `${Math.floor(attendMinutes)}분`;
    return '-';
};
const getNameText = (item, studentType) => {
    if (studentType === 'attend')
        return item?.conference_name || '-';
    return item?.test_name || item?.conference_name || item?.title || '-';
};
const getSubmitStatusText = (item) => (item?.done ? '응시' : '미응시');
const getScoreText = (item) => {
    if (!item?.done || item?.score === -1 || item?.score === undefined || item?.score === null)
        return '-';
    return String(item.score);
};
const getColumnSpecs = (studentType) => {
    const ratios = [0.85, 3.55, 1, 0.8];
    const headers = studentType === 'attend'
        ? ['출결일', '강의명', '출결여부', '참여시간']
        : ['응시일', studentType === 'test' ? '테스트명' : '과제명', '응시여부', '점수'];
    return { ratios, headers };
};
const getSummaryBlockHeight = () => SUMMARY_TITLE_HEIGHT + SUMMARY_CARD_HEIGHT + SUMMARY_BLOCK_GAP;
const clampPercentage = (value) => {
    if (!Number.isFinite(value))
        return 0;
    return Math.max(0, Math.min(100, value));
};
const getBlockHeight = (rowsCount) => SECTION_TITLE_HEIGHT + SECTION_TITLE_TABLE_GAP + TABLE_HEADER_HEIGHT + rowsCount * TABLE_ROW_HEIGHT;
const getRowsFit = (remainingHeight) => {
    const usable = remainingHeight - SECTION_TITLE_HEIGHT - SECTION_TITLE_TABLE_GAP - TABLE_HEADER_HEIGHT;
    if (usable < TABLE_ROW_HEIGHT)
        return 0;
    return Math.floor(usable / TABLE_ROW_HEIGHT);
};
const buildPagePlan = (payload, pageHeight) => {
    const maxContentHeight = pageHeight - PAGE_TOP - PAGE_BOTTOM - FOOTER_SPACE;
    const pages = [];
    const sectionState = payload.sections.map(section => ({
        ...section,
        index: 0,
        continueCount: 1,
    }));
    let sectionCursor = 0;
    let isFirstPage = true;
    let needsHistoryTitle = true;
    while (sectionCursor < sectionState.length) {
        let remaining = maxContentHeight;
        const page = {
            showSummary: isFirstPage,
            showHistoryTitle: false,
            blocks: [],
        };
        if (isFirstPage) {
            remaining -= getSummaryBlockHeight();
        }
        if (needsHistoryTitle) {
            if (remaining < HISTORY_TITLE_HEIGHT + TABLE_ROW_HEIGHT + TABLE_HEADER_HEIGHT + SECTION_TITLE_HEIGHT) {
                pages.push(page);
                isFirstPage = false;
                continue;
            }
            page.showHistoryTitle = true;
            remaining -= HISTORY_TITLE_HEIGHT;
            needsHistoryTitle = false;
        }
        while (sectionCursor < sectionState.length) {
            const section = sectionState[sectionCursor];
            const remainingRows = section.testData.length - section.index;
            if (remainingRows <= 0) {
                sectionCursor += 1;
                continue;
            }
            const availableRows = getRowsFit(remaining);
            if (availableRows <= 0)
                break;
            const take = Math.min(remainingRows, availableRows);
            const rows = section.testData.slice(section.index, section.index + take);
            const title = section.index === 0 ? section.title : `${section.title} (계속 ${section.continueCount})`;
            page.blocks.push({
                title,
                studentType: section.studentType,
                rows,
            });
            section.index += take;
            const blockHeight = getBlockHeight(take);
            remaining -= blockHeight;
            if (section.index < section.testData.length) {
                section.continueCount += 1;
                break;
            }
            sectionCursor += 1;
            if (sectionCursor < sectionState.length) {
                remaining -= BLOCK_GAP;
                if (remaining <= 0)
                    break;
            }
        }
        if (page.blocks.length === 0 && sectionCursor < sectionState.length) {
            const section = sectionState[sectionCursor];
            const remainingRows = section.testData.length - section.index;
            if (remainingRows <= 0) {
                sectionCursor += 1;
                pages.push(page);
                isFirstPage = false;
                continue;
            }
            page.blocks.push({
                title: section.index === 0 ? section.title : `${section.title} (계속 ${section.continueCount})`,
                studentType: section.studentType,
                rows: section.testData.slice(section.index, section.index + 1),
            });
            section.index += 1;
            if (section.index < section.testData.length) {
                section.continueCount += 1;
            }
            else {
                sectionCursor += 1;
            }
        }
        pages.push(page);
        isFirstPage = false;
    }
    if (pages.length === 0) {
        pages.push({
            showSummary: true,
            showHistoryTitle: true,
            blocks: [],
        });
    }
    return pages;
};
const drawSummary = (pdf, summaryCards, startY) => {
    const pageWidth = pdf.internal.pageSize.getWidth();
    const margin = 12;
    const contentWidth = pageWidth - margin * 2;
    const gap = 4;
    const boxWidth = (contentWidth - gap * 2) / 3;
    const boxY = startY + SUMMARY_TITLE_HEIGHT;
    pdf.setFontSize(20);
    pdf.text('학습 요약', margin, startY + 8);
    summaryCards.slice(0, 3).forEach((card, index) => {
        const x = margin + index * (boxWidth + gap);
        pdf.setDrawColor(220, 224, 230);
        pdf.setFillColor(249, 250, 252);
        pdf.roundedRect(x, boxY, boxWidth, SUMMARY_CARD_HEIGHT, 2, 2, 'FD');
        pdf.setFontSize(12);
        pdf.text(card.title, x + 4, boxY + 8);
        const contentX = x + 4;
        const contentWidth = boxWidth - 8;
        const barWidth = Math.max(16, Math.min(30, contentWidth));
        const barX = contentX + contentWidth - barWidth;
        const rowCenterY = boxY + 18;
        const barY = rowCenterY - SUMMARY_BAR_HEIGHT / 2;
        const percentage = clampPercentage(card.percentage);
        const fillWidth = (barWidth * percentage) / 100;
        const percentText = `${Math.round(percentage)}%`;
        pdf.setFontSize(20);
        pdf.text(percentText, contentX, rowCenterY + 2.8);
        pdf.setDrawColor(220, 224, 230);
        pdf.setFillColor(229, 230, 234);
        pdf.rect(barX, barY, barWidth, SUMMARY_BAR_HEIGHT, 'FD');
        if (fillWidth > 0) {
            pdf.setFillColor(SUMMARY_BAR_COLOR[0], SUMMARY_BAR_COLOR[1], SUMMARY_BAR_COLOR[2]);
            pdf.rect(barX, barY, fillWidth, SUMMARY_BAR_HEIGHT, 'F');
        }
        pdf.setFontSize(10);
        pdf.text(trimText(pdf, card.summaryLine, boxWidth - 8), x + 4, boxY + 32);
        pdf.text(`총 ${card.total}회 / 완료 ${card.done}회`, x + 4, boxY + 40);
    });
    return boxY + SUMMARY_CARD_HEIGHT + SUMMARY_BLOCK_GAP;
};
const drawSectionBlock = (pdf, block, startY, contentWidth) => {
    const margin = PAGE_MARGIN_X;
    const { ratios, headers } = getColumnSpecs(block.studentType);
    const ratioSum = ratios.reduce((acc, ratio) => acc + ratio, 0);
    const colWidths = ratios.map(ratio => (contentWidth * ratio) / ratioSum);
    pdf.setFontSize(14);
    pdf.text(block.title, margin, startY + 6);
    const tableTopY = startY + SECTION_TITLE_HEIGHT + SECTION_TITLE_TABLE_GAP;
    pdf.setDrawColor(196, 200, 206);
    pdf.setFillColor(243, 244, 248);
    pdf.rect(margin, tableTopY, contentWidth, TABLE_HEADER_HEIGHT, 'FD');
    let cursorX = margin;
    pdf.setFontSize(10);
    headers.forEach((header, idx) => {
        const width = colWidths[idx];
        pdf.rect(cursorX, tableTopY, width, TABLE_HEADER_HEIGHT);
        pdf.text(header, cursorX + width / 2, tableTopY + 5.5, { align: 'center' });
        cursorX += width;
    });
    const safeRows = block.rows.length > 0 ? block.rows : [{}];
    safeRows.forEach((row, rowIdx) => {
        const y = tableTopY + TABLE_HEADER_HEIGHT + rowIdx * TABLE_ROW_HEIGHT;
        let cellX = margin;
        const values = block.studentType === 'attend'
            ? [
                formatDate(row?.startline),
                getNameText(row, block.studentType),
                getAttendanceStatusText(row),
                getDurationText(row),
            ]
            : [
                formatDate(row?.startline),
                getNameText(row, block.studentType),
                getSubmitStatusText(row),
                getScoreText(row),
            ];
        values.forEach((value, idx) => {
            const width = colWidths[idx];
            pdf.rect(cellX, y, width, TABLE_ROW_HEIGHT);
            const isCenter = idx === 0 || idx >= 2;
            const text = trimText(pdf, String(value ?? '-'), width - 3);
            if (isCenter) {
                pdf.text(text, cellX + width / 2, y + 5.3, { align: 'center' });
            }
            else {
                pdf.text(text, cellX + 1.6, y + 5.3);
            }
            cellX += width;
        });
    });
    return tableTopY + TABLE_HEADER_HEIGHT + safeRows.length * TABLE_ROW_HEIGHT;
};
const addPageNumbers = (pdf) => {
    const totalPages = pdf.getNumberOfPages();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    for (let i = 1; i <= totalPages; i += 1) {
        pdf.setPage(i);
        pdf.setFontSize(9);
        pdf.text(`${i}/${totalPages}`, pageWidth / 2, pageHeight - 4, { align: 'center' });
    }
};
const buildStudyReportPdfDoc = async (payload) => {
    const pdf = new jspdf_1.default('p', 'mm', 'a4');
    pdf.setProperties({ title: 'study-report' });
    try {
        const fontBase64 = await loadKoreanFontBase64();
        pdf.addFileToVFS(FONT_FILE_NAME, fontBase64);
        pdf.addFont(FONT_FILE_NAME, FONT_FAMILY, 'normal');
        pdf.setFont(FONT_FAMILY, 'normal');
    }
    catch (error) {
        console.warn('Failed to load Korean font for PDF. Fallback font will be used.', error);
    }
    const pageHeight = pdf.internal.pageSize.getHeight();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const contentWidth = pageWidth - PAGE_MARGIN_X * 2;
    const pages = buildPagePlan(payload, pageHeight);
    pages.forEach((plan, pageIndex) => {
        if (pageIndex > 0) {
            pdf.addPage();
        }
        let cursorY = PAGE_TOP;
        if (plan.showSummary) {
            cursorY = drawSummary(pdf, payload.summaryCards || [], cursorY);
        }
        if (plan.showHistoryTitle) {
            pdf.setFontSize(18);
            pdf.text('학습 내역', PAGE_MARGIN_X, cursorY + 7);
            cursorY += HISTORY_TITLE_HEIGHT;
        }
        plan.blocks.forEach((block, index) => {
            cursorY = drawSectionBlock(pdf, block, cursorY, contentWidth);
            if (index < plan.blocks.length - 1) {
                cursorY += BLOCK_GAP;
            }
        });
    });
    addPageNumbers(pdf);
    return pdf;
};
const openStudyReportPdf = async (payload) => {
    APP.eventManager.publish('SHOW_LOADING', true);
    try {
        const pdf = await buildStudyReportPdfDoc(payload);
        return new Uint8Array(pdf.output('arraybuffer'));
    }
    finally {
        APP.eventManager.publish('SHOW_LOADING', false);
    }
};
exports.openStudyReportPdf = openStudyReportPdf;
const downloadPdf = async (payload) => {
    APP.eventManager.publish('SHOW_LOADING', true);
    try {
        const pdf = await buildStudyReportPdfDoc(payload);
        const blob = new Blob([pdf.output('arraybuffer')], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'study-report.pdf';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
    }
    finally {
        APP.eventManager.publish('SHOW_LOADING', false);
    }
};
exports.downloadPdf = downloadPdf;
